import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import jobIntelLogo from '@/assets/logos/jobintel-360-logo.png';
import { ReportMarkdownRenderer } from '@/components/ReportMarkdownRenderer';
import { 
  ArrowLeft, CheckCircle, AlertTriangle, XCircle, 
  DollarSign, User, TrendingUp, AlertOctagon, Flag,
  ExternalLink, FileText, ChevronDown, ChevronUp,
  Shield, Mail, Star, Briefcase, MapPin
} from 'lucide-react';

interface JobIntelReport {
  id: string;
  user_id: string;
  job_url: string | null;
  company_name: string | null;
  job_title: string | null;
  verdict: string | null;
  job_status: any;
  hiring_manager: any;
  pain_points: any;
  company_goals: any;
  resume_tips: string | null;
  report: string | null;
  status: string;
  error: string | null;
  message: string | null;
  created_at: string;
}

interface JobInsiderReportDetailProps {
  report: JobIntelReport;
  onBack: () => void;
}

export const JobInsiderReportDetail: React.FC<JobInsiderReportDetailProps> = ({ report, onBack }) => {
  const [showFullReport, setShowFullReport] = useState(true);

  const getVerdictConfig = (verdict: string | null) => {
    switch (verdict) {
      case 'APPLY':
        return { label: 'RECOMMENDED — Apply', icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' };
      case 'CAUTION':
        return { label: 'CAUTION — Proceed Carefully', icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' };
      case 'DONT_APPLY':
        return { label: 'NOT RECOMMENDED — Don\'t Apply', icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' };
      default:
        return { label: 'PENDING — Analysis In Progress', icon: Shield, color: 'text-gray-500', bg: 'bg-gray-50', border: 'border-gray-200' };
    }
  };

  if (report.status === 'failed') {
    return (
      <div className="min-h-screen bg-gray-100 py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <Button variant="ghost" onClick={onBack} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Reports
          </Button>
          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-2">Report Generation Failed</h3>
            <p className="text-gray-600">{report.message || report.error || "Unable to generate report"}</p>
          </div>
        </div>
      </div>
    );
  }

  const verdictConfig = getVerdictConfig(report.verdict);
  const VerdictIcon = verdictConfig.icon;
  const painPoints = Array.isArray(report.pain_points) ? report.pain_points : [];
  const companyGoals = Array.isArray(report.company_goals) ? report.company_goals : [];
  const hiringManager = report.hiring_manager || {};
  const jobStatus = report.job_status || {};
  const reportText = report.report || '';

  const getSalarySection = () => {
    const lines = reportText.split('\n');
    const salaryLines: string[] = [];
    let inSalary = false;
    for (const line of lines) {
      if (/compensation|salary|pay|benefits/i.test(line) && /^#{1,3}\s/.test(line)) { inSalary = true; continue; }
      if (inSalary && /^#{1,3}\s/.test(line)) break;
      if (inSalary && line.trim()) salaryLines.push(line.trim());
    }
    return salaryLines;
  };

  const getRedFlagsSection = () => {
    const lines = reportText.split('\n');
    const flagLines: string[] = [];
    let inSection = false;
    for (const line of lines) {
      if (/red flag|caution|risk|concern/i.test(line) && /^#{1,3}\s/.test(line)) { inSection = true; continue; }
      if (inSection && /^#{1,3}\s/.test(line)) break;
      if (inSection && line.trim()) flagLines.push(line.trim());
    }
    return flagLines;
  };

  const salaryLines = getSalarySection();
  const redFlags = getRedFlagsSection();

  return (
    <div className="min-h-screen bg-gray-200 py-6 px-4">
      <div className="max-w-[750px] mx-auto">
        <Button variant="ghost" onClick={onBack} className="mb-4 text-gray-700 hover:text-gray-900">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Reports
        </Button>

        {/* Main report page */}
        <div className="bg-white rounded-md shadow-[0_4px_40px_rgba(0,0,0,0.12)] overflow-hidden">
          
          {/* Top blue accent line */}
          <div className="h-1.5 bg-[#1a3a6b]" />

          {/* Logo & Tagline */}
          <div className="pt-8 pb-2 text-center">
            <img src={jobIntelLogo} alt="JobIntel 360" className="h-20 mx-auto object-contain" />
            <p className="text-sm italic text-[#1a3a6b] mt-1 font-medium">Your Unfair Advantage in the Job Market</p>
          </div>

          {/* Report Title */}
          <div className="text-center pb-6 px-8">
            <h2 className="text-[28px] font-extrabold text-gray-900 leading-tight">
              The Job Insider Report
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              {report.company_name || 'Unknown Company'} — {report.job_title || 'Unknown Title'}
            </p>
          </div>

          {/* Verdict Banner */}
          <div className="mx-8 mb-6">
            <div className={`${verdictConfig.bg} ${verdictConfig.border} border rounded-md px-5 py-3 flex items-center gap-3`}>
              <VerdictIcon className={`w-6 h-6 ${verdictConfig.color} shrink-0`} />
              <span className={`font-bold text-sm tracking-wide uppercase ${verdictConfig.color}`}>{verdictConfig.label}</span>
            </div>
          </div>

          {/* ─── Intel Category Quick Stats ─── */}
          <ReportSection title="Intel Category Quick Stats">
            <div className="border border-gray-200 rounded-md">
              <div className="grid grid-cols-2 divide-x divide-gray-200">
                {/* Left column */}
                <div className="p-4 space-y-3">
                  <QuickStatRow icon={CheckCircle} label="Valid Job Posting" value={jobStatus.is_active !== false ? '✓' : '✗'} />
                  {hiringManager.name && (
                    <QuickStatRow icon={User} label="Hiring Manager" value={`${hiringManager.name}${hiringManager.title ? `, ${hiringManager.title}` : ''}`} />
                  )}
                  {salaryLines.length > 0 && (
                    <QuickStatRow icon={DollarSign} label="Est. Salary" value={cleanMarkdown(salaryLines[0])} />
                  )}
                </div>
                {/* Right column */}
                <div className="p-4 space-y-3">
                  {hiringManager.name && (
                    <QuickStatRow icon={Mail} label="Hiring Manager" value={`${hiringManager.name}${hiringManager.title ? ' at ' + hiringManager.title : ''}`} />
                  )}
                  {salaryLines.length > 1 && (
                    <QuickStatRow icon={DollarSign} label="Compensation" value={cleanMarkdown(salaryLines[1])} />
                  )}
                  <QuickStatRow icon={Shield} label="Verdict" value={report.verdict === 'APPLY' ? 'Safe to Apply' : report.verdict === 'CAUTION' ? 'Proceed with Caution' : report.verdict === 'DONT_APPLY' ? 'Avoid' : 'Pending'} />
                </div>
              </div>
            </div>
          </ReportSection>

          {/* ─── Who to Impress (Hiring Manager) ─── */}
          {hiringManager.name && (
            <ReportSection title="Hiring Manager">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-1">
                  <p className="text-lg font-bold text-gray-900">{hiringManager.name}</p>
                  {hiringManager.title && (
                    <p className="text-sm text-[#1a3a6b] font-semibold">{hiringManager.title}</p>
                  )}
                  {hiringManager.linkedin_url && (
                    <a href={hiringManager.linkedin_url} target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1 mt-2 bg-[#0077b5] text-white text-xs font-bold px-3 py-1 rounded hover:bg-[#005f8d] transition-colors">
                      LinkedIn <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                {/* Right: pain points preview */}
                {painPoints.length > 0 && (
                  <div>
                    <p className="font-bold text-gray-900 text-sm mb-2">Top Problems Facing the Company & Department</p>
                    <ul className="space-y-2">
                      {painPoints.slice(0, 3).map((pp: any, i: number) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="text-[#1a3a6b] mt-0.5 shrink-0">•</span>
                          <span>{pp.point || pp.title}{pp.impact ? `. ${pp.impact}` : ''}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </ReportSection>
          )}

          {/* ─── Salary Estimate ─── */}
          {salaryLines.length > 0 && (
            <ReportSection title="Salary Estimate">
              <div className="grid grid-cols-2 gap-6">
                <ul className="space-y-1.5">
                  {salaryLines.slice(0, 5).map((line, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="text-[#1a3a6b] mt-0.5 shrink-0">•</span>
                      <span>{cleanMarkdown(line)}</span>
                    </li>
                  ))}
                </ul>
                {salaryLines.length > 5 && (
                  <ul className="space-y-1.5">
                    {salaryLines.slice(5, 10).map((line, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="text-[#1a3a6b] mt-0.5 shrink-0">•</span>
                        <span>{cleanMarkdown(line)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </ReportSection>
          )}

          {/* ─── Pain Points (full) ─── */}
          {painPoints.length > 0 && !hiringManager.name && (
            <ReportSection title="Top Problems Facing the Company & Department">
              <ul className="space-y-3">
                {painPoints.map((pp: any, i: number) => (
                  <li key={i}>
                    <p className="font-semibold text-gray-900 text-sm">{pp.point || pp.title}</p>
                    {pp.impact && <p className="text-gray-600 text-sm mt-0.5">{pp.impact}</p>}
                    {pp.advice && (
                      <div className="mt-1.5 bg-blue-50 border border-blue-200 rounded px-3 py-2">
                        <p className="text-xs font-semibold text-[#1a3a6b] mb-0.5 uppercase tracking-wide">Strategy</p>
                        <p className="text-sm text-gray-700">{pp.advice}</p>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </ReportSection>
          )}

          {/* ─── Red Flags ─── */}
          {redFlags.length > 0 && (
            <ReportSection title="Hidden Red Flags" titleColor="text-red-700">
              <ul className="space-y-1.5">
                {redFlags.slice(0, 6).map((line, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <Flag className="w-3.5 h-3.5 text-red-500 mt-0.5 shrink-0" />
                    <span>{cleanMarkdown(line)}</span>
                  </li>
                ))}
              </ul>
            </ReportSection>
          )}

          {/* ─── Resume Tips ─── */}
          {report.resume_tips && (
            <ReportSection title="Resume Tips">
              <p className="text-sm text-gray-700 leading-relaxed">{report.resume_tips}</p>
            </ReportSection>
          )}

          {/* CTA Button */}
          <div className="px-8 pb-8 pt-2">
            <button
              onClick={() => setShowFullReport(!showFullReport)}
              className="w-full bg-[#1a3a6b] hover:bg-[#15305a] text-white font-extrabold text-sm tracking-widest uppercase py-4 rounded-md transition-colors"
            >
              {showFullReport ? '>> HIDE FULL REPORT <<' : '>> CLICK TO VIEW FULL REPORT <<'}
            </button>
          </div>

          {/* Bottom blue accent line */}
          <div className="h-1.5 bg-[#1a3a6b]" />
        </div>

        {/* Full Report Expansion */}
        {showFullReport && (
          <div className="mt-6 bg-white rounded-md shadow-lg border border-gray-200 p-8">
            <ReportMarkdownRenderer content={report.report || ''} />
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Sub-components ─── */

function ReportSection({ title, titleColor, children }: { title: string; titleColor?: string; children: React.ReactNode }) {
  return (
    <div className="mx-8 mb-6">
      <div className="flex items-center gap-3 mb-3">
        <h3 className={`font-extrabold text-base ${titleColor || 'text-gray-900'}`}>{title}</h3>
        <div className="flex-1 h-px bg-gray-300" />
      </div>
      {children}
    </div>
  );
}

function QuickStatRow({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-4 h-4 text-[#1a3a6b] mt-0.5 shrink-0" />
      <div className="min-w-0">
        <span className="font-bold text-sm text-gray-900">{label}: </span>
        <span className="text-sm text-gray-700">{value}</span>
      </div>
    </div>
  );
}

function cleanMarkdown(text: string): string {
  return text
    .replace(/^[-*•]\s*/, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/^#+\s*/, '')
    .trim();
}
